import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { AgGridReact } from 'ag-grid-react';
import { GridApi, GridOptions, ICellRendererParams } from 'ag-grid-community';
import { LicenseManager } from 'ag-grid-enterprise';
import { useTheme } from '@material-ui/core';
import { APIAlert, FlexColumnDiv, Loader } from '@mummssoftware/common-ui';
import { FaxLogs, isResponse, FaxLogRecord } from '@mummssoftware/utils';
import styled from 'styled-components';

import 'ag-grid-community/dist/styles/ag-grid.css';
import '@mummssoftware/common-ui/style/css/ag-theme-hummingbird.css';

import { FaxLogContext } from '../../context';
import { getFaxLogColumnDefs } from './columnDefs';
import { FaxDetailRenderer } from './FaxDetailRenderer';
import { FaxPdfViewerModal } from './FaxPdfViewerModal';
import defaults from '../../translations/en.json';

LicenseManager &&
  LicenseManager.setLicenseKey(process.env.REACT_APP_AG_GRID_KEY as string);

const GridWrapper = styled.div`
  background-color: ${(props) => props.theme.palette.background.paper};
  overflow: auto;
  height: 90%;
  flex-grow: 1;

  /* center-align only styles the label; theme padding + sort icon still offset the header */
  &.ag-theme-hummingbird .ag-header-cell.center-align {
    padding-left: 0;
    padding-right: 0;
    justify-content: center;
  }

  &.ag-theme-hummingbird .ag-header-cell.center-align .ag-header-cell-label {
    justify-content: center;
    width: 100%;
    margin-left: 0;
    position: relative;
  }

  &.ag-theme-hummingbird
    .ag-header-cell.center-align
    .ag-header-cell-label
    .ag-header-icon {
    position: absolute;
    right: 2px;
    margin-left: 0;
  }

  .ag-details-row {
    animation: fax-detail-row-in 0.22s ease-out;
  }

  @keyframes fax-detail-row-in {
    from {
      opacity: 0;
      transform: translateY(-6px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

function normalizeRows(data: unknown): FaxLogRecord[] {
  if (Array.isArray(data)) {
    return data as FaxLogRecord[];
  }
  if (data && typeof data === 'object') {
    const o = data as Record<string, unknown>;
    if (Array.isArray(o.content)) {
      return o.content as FaxLogRecord[];
    }
    if (Array.isArray(o.data)) {
      return o.data as FaxLogRecord[];
    }
    if (Array.isArray(o.items)) {
      return o.items as FaxLogRecord[];
    }
  }
  return [];
}

export const FaxLogGrid = () => {
  const { agency, keycloak, smallWidget } = useContext(FaxLogContext);
  const [gridApi, setGridApi] = useState<GridApi | null>(null);
  const [rowData, setRowData] = useState<FaxLogRecord[] | null>(null);
  const [errorInfo, setErrorInfo] = useState<mumms.ErrorInfo | null>(null);
  const [openPdfRow, setOpenPdfRow] = useState<FaxLogRecord | null>(null);

  const {
    props: { grid: gridTheme },
  } = useTheme() as any;

  const columnDefs = useMemo(() => getFaxLogColumnDefs(!!smallWidget), [
    smallWidget,
  ]);

  const FaxDetailRow = useCallback(
    (params: ICellRendererParams) => (
      <FaxDetailRenderer
        data={params.data as FaxLogRecord}
        onOpenPdf={(row) => setOpenPdfRow(row)}
      />
    ),
    [],
  );

  const frameworkComponents = useMemo(
    () => ({
      agDetailCellRenderer: FaxDetailRow,
    }),
    [FaxDetailRow],
  );

  const expandRow = useCallback(
    (params: any) => {
      if (smallWidget || params.event?.defaultPrevented) {
        return;
      }
      if (params.colDef?.field === 'expandToggle') {
        return;
      }
      params.node.setExpanded(!params.node.expanded);
    },
    [smallWidget],
  );

  const gridOptions: GridOptions = useMemo(
    () => ({
      defaultColDef: {
        resizable: true,
        sortable: true,
        filter: 'agTextColumnFilter',
        menuTabs: ['filterMenuTab'],
      },
      masterDetail: !smallWidget,
      embedFullWidthRows: true,
      isRowMaster: () => !smallWidget,
      suppressCellSelection: true,
      onGridReady: (params: { api: GridApi }) => {
        setGridApi(params.api);
        params.api.sizeColumnsToFit();
      },
    }),
    [smallWidget],
  );

  useEffect(() => {
    if (!agency || !keycloak) {
      return undefined;
    }

    const baseUrl =
      window.env?.hosts?.faxLogServiceUrl ||
      window.env?.hosts?.faxLogServiceURL;

    if (!baseUrl || typeof baseUrl !== 'string') {
      setErrorInfo({
        text: defaults.missingServiceUrl as string,
        severity: 'error',
      });
      setRowData([]);
      return undefined;
    }

    const client = new FaxLogs({
      url: baseUrl,
      keycloak,
      agency,
    });

    let cancelled = false;

    client
      .listFaxLogs()
      .then((res) => {
        if (cancelled) {
          return;
        }
        if (isResponse(res)) {
          setErrorInfo({
            text: `${res.status} ${res.statusText || defaults.loadError}`,
            severity: 'error',
          });
          setRowData([]);
          return;
        }
        const rows = normalizeRows(res);
        setRowData(rows);
        setErrorInfo(null);
      })
      .catch(() => {
        if (!cancelled) {
          setErrorInfo({
            text: defaults.loadError as string,
            severity: 'error',
          });
          setRowData([]);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [agency, keycloak]);

  useEffect(() => {
    if (gridApi && rowData) {
      gridApi.setRowData(rowData);
      gridApi.setSortModel([{ colId: 'faxDateTime_shifted', sort: 'desc' }]);
    }
  }, [gridApi, rowData]);

  if (errorInfo && errorInfo.text === defaults.missingServiceUrl) {
    return (
      <FlexColumnDiv style={{ padding: 16 }}>
        <APIAlert
          errorInfo={errorInfo}
          persistentError
          handleClose={() => setErrorInfo(null)}
        />
      </FlexColumnDiv>
    );
  }

  if (rowData === null) {
    return <Loader />;
  }

  return (
    <FlexColumnDiv style={{ height: '100%' }}>
      {errorInfo ? (
        <APIAlert
          errorInfo={errorInfo}
          handleClose={() => setErrorInfo(null)}
        />
      ) : null}
      <GridWrapper
        id="fax-log-table-wrapper"
        className={`${gridTheme.className} light`}
      >
        <AgGridReact
          gridOptions={gridOptions}
          columnDefs={columnDefs}
          rowData={rowData}
          rowHeight={gridTheme.rowHeight}
          headerHeight={gridTheme.headerHeight}
          frameworkComponents={frameworkComponents}
          detailRowAutoHeight
          onCellClicked={expandRow}
        />
      </GridWrapper>
      <FaxPdfViewerModal row={openPdfRow} onClose={() => setOpenPdfRow(null)} />
    </FlexColumnDiv>
  );
};
